import { EventType } from "@/analytics/analytics-events";
import { EditElementProperties } from "@/analytics/data-organization-events-properties";
import { useWaypointReference } from "@/components/r3f/utils/placeholder-preview";
import { isIElementWaypoint } from "@/hooks/use-placeholders";
import { EditWaypointColor } from "@/modes/sheet-mode/edit-waypoints/edit-waypoint-context-menu/edit-waypoint-color";
import { useAppSelector } from "@/store/store-hooks";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { isIElementRoomSection } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { useCallback, type ComponentProps, type JSX } from "react";
import { ContextMenuActionType, type ContextMenuAction } from "../action-types";

function EditWaypointColorActionComponent({
  elementId,
  dark,
  closeContextMenu,
}: ComponentProps<
  NonNullable<ContextMenuAction["CustomComponent"]>
>): JSX.Element | null {
  const element = useAppSelector(selectIElement(elementId));

  const waypoint = element && isIElementWaypoint(element) ? element : undefined;

  const reference = useWaypointReference(waypoint);

  const onColorEdited = useCallback(() => {
    assert(
      reference,
      "Reference must be defined to track a capture-tree-context-menu action",
    );
    Analytics.track<EditElementProperties>(EventType.editElement, {
      via: "capture tree context menu",
      elementType: reference.type,
      elementTypeHint: reference.typeHint ?? undefined,
      hasEditedName: false,
      hasEditedTimePoint: false,
      hasEditedTags: false,
      hasEditedColor: true,
    });

    closeContextMenu();
  }, [closeContextMenu, reference]);

  if (!reference || !isIElementRoomSection(reference)) return null;

  return (
    <EditWaypointColor
      dark={dark}
      waypointReference={reference}
      onColorChanged={onColorEdited}
    />
  );
}

/**
 * Action to edit the color of a waypoint.
 * It uses a custom component to render the color picker.
 */
export const EDIT_WAYPOINT_COLOR_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.editWaypointColor,
  CustomComponent: EditWaypointColorActionComponent,
};
